import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  History,
  Loader2,
  Wrench,
} from "lucide-react";
import { apiRequest } from "@/services/api-client";

interface StatusEvent {
  id: string;
  type: "INCIDENT" | "MAINTENANCE";
  title: string;
  provider?: string | null;
  payment_method?: string | null;
  country_code?: string | null;
  country_name?: string | null;
  service_name: string;
  status: string;
  severity: string;
  body: string;
  affected_services?: string[] | null;
  status_updates?: { status: string; body: string; at: string }[] | null;
  started_at?: string | null;
  scheduled_start_at?: string | null;
  scheduled_end_at?: string | null;
  resolved_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface StatusPayload {
  events: StatusEvent[];
}
type StatusTab = "INCIDENT" | "MAINTENANCE";

const humanize = (value: string) =>
  value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const dateTime = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "UTC",
      }).format(new Date(value))
    : "À confirmer";
const eventUrl = (event: StatusEvent) => `/incidents/${event.id}`;
const severityKey = (severity: string) => severity.toUpperCase();
const severityLabel = (severity: string) =>
  ({
    MINOR: "Gravité mineure",
    MAJOR: "Gravité majeure",
    CRITICAL: "Gravité critique",
  })[severityKey(severity)] ?? "Gravité non définie";
const severityBadgeClass = (severity: string) =>
  ({
    MINOR: "border-cyan-200 bg-cyan-50 text-cyan-800",
    MAJOR: "border-amber-200 bg-amber-50 text-amber-800",
    CRITICAL: "border-rose-200 bg-rose-50 text-rose-800",
  })[severityKey(severity)] ?? "border-slate-200 bg-slate-50 text-slate-700";
const severityHeroClass = (severity: string) =>
  ({
    MINOR: "from-[#0c2c68] via-[#075bd1] to-[#08a9c7]",
    MAJOR: "from-[#7a3b08] via-[#d97706] to-[#fbbf24]",
    CRITICAL: "from-[#701c2a] via-[#c6283d] to-[#ef4444]",
  })[severityKey(severity)] ?? "from-[#0c2c68] via-[#075bd1] to-[#08a9c7]";

function usePublicStatusEvents() {
  const [events, setEvents] = useState<StatusEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    apiRequest<StatusPayload>("public", "/status/events", {
      authenticated: false,
    })
      .then(({ data }) => {
        if (active) setEvents(data.events ?? []);
      })
      .catch(() => {
        if (active)
          setError(
            "Les informations de statut sont temporairement indisponibles.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { events, loading, error };
}

function StatusHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <>
      <header className="border-b bg-white">
        <div className="mx-auto flex h-[72px] max-w-6xl items-center px-5">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-xl font-black tracking-tight text-[#102b62]"
          >
            <img
              src="/brand/bizconnectpay-symbol.png"
              alt=""
              className="h-8 w-8 object-contain"
            />
            <span>
              BizConnect<span className="text-blue-600">Pay</span>{" "}
              <span className="text-slate-500">Status</span>
            </span>
          </a>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 pb-16">
        <div className="py-10">
          <p className="text-xs font-extrabold uppercase tracking-[.18em] text-blue-600">
            Transparence opérationnelle
          </p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            {title}
          </h1>
          <p className="mt-3 max-w-3xl leading-7 text-slate-600">{subtitle}</p>
        </div>
      </main>
    </>
  );
}

function StatusNav({ active }: { active: "current" | "history" | "uptime" }) {
  const links = [
    { href: "/", label: "État actuel", key: "current", icon: Activity },
    { href: "/history", label: "Historique", key: "history", icon: History },
    { href: "/uptime", label: "Disponibilité", key: "uptime", icon: Clock3 },
  ] as const;
  return (
    <nav
      aria-label="Navigation du statut"
      className="mb-6 flex flex-wrap gap-2 border-b border-slate-200"
    >
      {links.map(({ href, label, key, icon: Icon }) => (
        <a
          key={key}
          href={href}
          className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold ${active === key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
        >
          <Icon size={16} />
          {label}
        </a>
      ))}
    </nav>
  );
}

function EventCard({ event }: { event: StatusEvent }) {
  const active =
    !event.resolved_at &&
    !["RESOLVED", "COMPLETED", "CANCELLED"].includes(event.status);
  const location = [
    event.country_name,
    event.provider,
    event.payment_method,
    event.service_name,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold ${severityBadgeClass(event.severity)}`}
        >
          {event.type === "INCIDENT" ? (
            <Activity size={14} />
          ) : (
            <Wrench size={14} />
          )}
          {humanize(event.status)}
        </span>
        <span
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${severityBadgeClass(event.severity)}`}
        >
          {severityLabel(event.severity)}
        </span>
        <span className="text-xs text-slate-500">
          {event.type === "MAINTENANCE" && event.scheduled_start_at
            ? `Début ${dateTime(event.scheduled_start_at)} UTC`
            : dateTime(event.started_at ?? event.created_at) + " UTC"}
        </span>
      </div>
      <h2 className="mt-3 text-lg font-bold text-slate-900">
        <a
          className="hover:text-blue-700 hover:underline"
          href={eventUrl(event)}
        >
          {event.title}
        </a>
      </h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">{event.body}</p>
      <p className="mt-3 text-xs text-slate-500">{location}</p>
      <a
        href={eventUrl(event)}
        className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-blue-700 hover:underline"
      >
        Voir les détails <ArrowUpRight size={15} />
      </a>
      {active && <span className="sr-only">Événement en cours</span>}
    </article>
  );
}

export function PublicStatusPage() {
  const { events, loading, error } = usePublicStatusEvents();
  const [tab, setTab] = useState<StatusTab>("INCIDENT");
  const activeIncidents = events.filter(
    (event) =>
      event.type === "INCIDENT" &&
      !event.resolved_at &&
      event.status !== "RESOLVED",
  );
  const visible = events.filter((event) => event.type === tab);
  return (
    <div className="min-h-screen bg-slate-50">
      <StatusHeader
        title="État des services"
        subtitle="Consultez les incidents et maintenances susceptibles d’affecter les paiements et services BizConnectPay."
      />
      <div className="mx-auto max-w-6xl px-5 pb-16">
        <div
          className={`mb-6 flex items-center gap-3 rounded-xl border p-4 font-semibold ${activeIncidents.length ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
        >
          <CheckCircle2 size={20} />
          {activeIncidents.length
            ? `${activeIncidents.length} incident(s) en cours sur nos services.`
            : "Aucun incident public en cours."}
        </div>
        <StatusNav active="current" />
        <div className="mb-5 flex gap-2">
          {(
            [
              { key: "INCIDENT", label: "Incidents", icon: Activity },
              { key: "MAINTENANCE", label: "Maintenance", icon: CalendarDays },
            ] as const
          ).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold ${tab === key ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}
            >
              <Icon size={16} />
              {label}
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${tab === key ? "bg-white/20" : "bg-slate-100"}`}
              >
                {events.filter((event) => event.type === key).length}
              </span>
            </button>
          ))}
        </div>
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : visible.length ? (
          <div className="grid gap-4">
            {visible.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <Empty
            message={
              tab === "INCIDENT"
                ? "Aucun incident publié."
                : "Aucune maintenance publiée."
            }
          />
        )}
      </div>
    </div>
  );
}

export function PublicStatusHistoryPage() {
  const { events, loading, error } = usePublicStatusEvents();
  const months = useMemo(() => {
    const groups = new Map<string, StatusEvent[]>();
    for (const event of [...events].sort(
      (a, b) =>
        Date.parse(b.started_at ?? b.created_at) -
        Date.parse(a.started_at ?? a.created_at),
    )) {
      const key = (event.started_at ?? event.created_at).slice(0, 7);
      groups.set(key, [...(groups.get(key) ?? []), event]);
    }
    return [...groups];
  }, [events]);
  return (
    <div className="min-h-screen bg-slate-50">
      <StatusHeader
        title="Historique des incidents et maintenances"
        subtitle="Retrouvez les événements publiés et leurs mises à jour, classés par mois."
      />
      <div className="mx-auto max-w-6xl px-5 pb-16">
        <StatusNav active="history" />
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : months.length ? (
          months.map(([month, items]) => (
            <section key={month} className="mb-10">
              <h2 className="mb-4 text-2xl font-bold text-slate-900">
                {new Intl.DateTimeFormat("fr-FR", {
                  month: "long",
                  year: "numeric",
                  timeZone: "UTC",
                }).format(new Date(`${month}-01T00:00:00Z`))}
              </h2>
              <div className="grid gap-4">
                {items.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </section>
          ))
        ) : (
          <Empty message="Aucun événement public n’a encore été publié." />
        )}
      </div>
    </div>
  );
}

export function PublicStatusUptimePage() {
  const { events, loading, error } = usePublicStatusEvents();
  const affected = new Set(
    events
      .filter((event) => !event.resolved_at && event.status !== "RESOLVED")
      .map((event) => event.service_name),
  );
  const services = [...new Set(events.map((event) => event.service_name))];
  return (
    <div className="min-h-screen bg-slate-50">
      <StatusHeader
        title="Disponibilité des services"
        subtitle="État opérationnel des services ayant des événements de statut publiés."
      />
      <div className="mx-auto max-w-6xl px-5 pb-16">
        <StatusNav active="uptime" />
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : services.length ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {services.map((service) => (
              <div
                key={service}
                className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 last:border-0"
              >
                <span className="font-semibold text-slate-800">{service}</span>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-bold ${affected.has(service) ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
                >
                  {affected.has(service)
                    ? "Perturbation signalée"
                    : "Opérationnel"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <Empty message="Aucune donnée de disponibilité n’est encore publiée." />
        )}
      </div>
    </div>
  );
}

export function PublicStatusDetailPage({ id }: { id: string }) {
  const { events, loading, error } = usePublicStatusEvents();
  const event = events.find((item) => item.id === id);
  const updates = event?.status_updates?.length
    ? [...event.status_updates].sort(
        (a, b) => Date.parse(b.at) - Date.parse(a.at),
      )
    : event
      ? [{ status: event.status, body: event.body, at: event.updated_at }]
      : [];
  const affected = event
    ? [
        event.country_name,
        event.provider,
        event.payment_method,
        event.service_name,
        ...(event.affected_services ?? []),
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
  return (
    <div className="min-h-screen bg-[#fafafa]">
      <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
        <a
          href="/"
          className="mb-7 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:underline"
        >
          <ArrowLeft size={16} /> État actuel
        </a>
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : !event ? (
          <Empty message="Cet événement est introuvable ou n’est plus public." />
        ) : (
          <>
            <header
              className={`rounded-2xl bg-gradient-to-br ${severityHeroClass(event.severity)} px-6 py-7 text-white shadow-lg shadow-slate-950/10 sm:px-9 sm:py-8`}
            >
              <h1 className="text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                {event.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-blue-50">
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-bold text-slate-900">
                  <span className="h-2 w-2 rounded-full bg-slate-500" />
                  {humanize(event.status)}
                </span>
                <span className="rounded-full border border-white/40 bg-white/15 px-3 py-1.5 font-semibold text-white">
                  {severityLabel(event.severity)}
                </span>
                <span>
                  {event.type === "MAINTENANCE"
                    ? `Début ${dateTime(event.scheduled_start_at)} UTC`
                    : `Signalé ${dateTime(event.started_at ?? event.created_at)} UTC`}
                </span>
                <span>Mis à jour {dateTime(event.updated_at)} UTC</span>
              </div>
            </header>
            <section className="my-10 rounded-md border border-slate-200 bg-white px-5 py-4 text-base leading-7 text-slate-800 sm:my-14 sm:text-lg">
              <strong className="mr-3">Services concernés</strong>
              {affected}
            </section>
            <section
              aria-label="Historique des mises à jour"
              className="space-y-10"
            >
              {updates.map((update, index) => (
                <article
                  key={`${update.at}-${index}`}
                  className="grid gap-2 sm:grid-cols-[220px_1fr] sm:gap-10"
                >
                  <h2 className="font-bold text-slate-900">
                    {humanize(update.status)}
                  </h2>
                  <div className="text-base leading-7 text-slate-800">
                    {update.body}
                    <span className="mt-1 block text-sm text-slate-500">
                      Publié {dateTime(update.at)} UTC
                    </span>
                  </div>
                </article>
              ))}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex min-h-40 items-center justify-center gap-3 text-sm text-slate-500">
      <Loader2 className="animate-spin" size={20} />
      Chargement des informations…
    </div>
  );
}
function ErrorMessage({ children }: { children: string }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800"
    >
      {children}
    </div>
  );
}
function Empty({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-600">
      {message}
    </div>
  );
}
